import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { History } from 'lucide-react';
import clsx from 'clsx';
import { usePanel } from 'hooks/usePanel';
import { Sizes } from 'enums/sizes';
import { NsKey } from 'enums/NsKey';
import { Button } from 'components/ui/Button';
import { TimelinePanel } from '../TimelinePanel';

interface TimelineButtonProps {
  riskId?: string;
  borrowerProjectId?: string;
  unreadCount?: number;
  /**
   * Appelé à l'ouverture du panel — typiquement pour remettre le badge à 0
   * (consulter l'historique marque tout lu côté back ; on reflète sans refresh).
   */
  onOpen?: () => void;
  /** Classes additionnelles du bouton (ex. w-full pour l'aligner dans une grille). */
  className?: string;
}

/**
 * Floating button that opens the activity timeline panel.
 * Positioned in the top-right of the subscription process header.
 */
export const TimelineButton: React.FC<TimelineButtonProps> = ({
  riskId,
  borrowerProjectId,
  unreadCount = 0,
  onOpen,
  className
}) => {
  const { t } = useTranslation(NsKey.Common);
  const { openPanel } = usePanel();

  const handleOpenTimeline = useCallback(() => {
    onOpen?.();
    openPanel(
      {
        title: t('ACTIVITY_TIMELINE.PANEL_TITLE'),
        size: Sizes.lg
      },
      <TimelinePanel riskId={riskId} borrowerProjectId={borrowerProjectId} />
    );
  }, [openPanel, t, riskId, borrowerProjectId, onOpen]);

  const hasUnread = unreadCount > 0;
  const displayCount = unreadCount > 99 ? '99+' : unreadCount;

  return (
    <div className="relative">
      <Button
        round
        color="white"
        onClick={handleOpenTimeline}
        className={clsx(
          '!px-2 !py-1.5 shadow-sm text-sm',
          hasUnread && 'ring-2 ring-primary/20',
          className
        )}
      >
        <History
          data-slot="icon"
          className={clsx('!w-4 !h-4', hasUnread ? '!text-primary' : '!text-gray-500')}
        />
        <span className={clsx('whitespace-nowrap', hasUnread ? 'text-primary' : 'text-gray-700')}>
          {t('ACTIVITY_TIMELINE.BUTTON_LABEL')}
        </span>
      </Button>

      {/* Badge for unread count */}
      {hasUnread && (
        <span
          className={clsx(
            'absolute -top-1.5 -right-1.5',
            'flex items-center justify-center',
            'min-w-[20px] h-5 px-1.5',
            'bg-primary text-white',
            'text-xs font-semibold',
            'rounded-full',
            'ring-2 ring-white',
            'animate-in zoom-in-50 duration-200'
          )}
        >
          {displayCount}
        </span>
      )}
    </div>
  );
};

export default TimelineButton;
